import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logo from '../../assets/logo-aft4.png';
import signature from '../../assets/Cachet et Signature AF4T.png';
import {
    dateFormatter,
    priceFormatter,
    toEuro,
    toFrench,
} from '../../settings/themeSettings';
import { getClientSageAuxCode } from '../../utils/sageAccountingEngine';

// ─── Couleurs ────────────────────────────────────────────────────────────────
const BLACK = '#000000';
const NAVY = '#000000';
const BLUE = '#000000';
const GRAY = '#999999';

// ─── Largeurs colonnes ────────────────────────────────────────────────────────
const CW_BOTH = {
    ref: '12%',
    design: '30%',
    nature: '12%',
    nbrPax: '6%',
    qte: '6%',
    prixCFA: '12%',
    prixEUR: '10%',
    montant: '12%'
};

const CW_SINGLE = {
    ref: '12%',
    design: '30%',
    nature: '12%',
    nbrPax: '6%',
    qte: '6%',
    prix: '22%',
    montant: '12%'
};

// ── Styles génériques ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({

    // PAGE
    page: {
        backgroundColor: '#ffffff',
        paddingTop: 15,
        paddingBottom: 55,
        paddingHorizontal: 28,
        fontSize: 8,
        color: BLACK,
        flexDirection: 'column',
    },

    // MENU (coin supérieur droit)
    menuRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: 2,
    },
    menuText: {
        fontSize: 7.5,
        color: BLUE,
        textDecoration: 'underline',
    },

    // LOGO (centré)
    logoRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: 8,
    },
    logo: { height: 55, width: 145 },

    // ── BLOC EN-TÊTE : DEUX COLONNES (Facture + Client) ──
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2,
        gap: 15,
    },

    // Bloc gauche (Facture Info)
    leftBlock: {
        width: '42%',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    originalText: {
        fontSize: 18,
        fontFamily: 'Helvetica-BoldOblique',
        color: BLACK,
        marginBottom: 2,
        letterSpacing: 1,
    },
    factureBadge: {
        backgroundColor: BLACK,
        paddingVertical: 3,
        paddingHorizontal: 6,
        alignItems: 'center',
        marginBottom: 2,
        borderRadius: 2,
    },
    factureBadgeText: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: '#ffffff',
        letterSpacing: 1,
    },

    // Mini tableau Numéro / Date / Dossier
    miniTable: {
        borderWidth: 0.5,
        borderColor: GRAY,
    },
    miniHead: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: GRAY,
        backgroundColor: '#f5f5f5',
    },
    miniRow: {
        flexDirection: 'row',
    },
    miniCellHead: {
        flex: 1,
        paddingVertical: 2,
        paddingHorizontal: 3,
        fontSize: 7.5,
        textAlign: 'center',
        borderRightWidth: 0.5,
        borderRightColor: GRAY,
    },
    miniCellHeadLast: {
        flex: 1,
        paddingVertical: 2,
        paddingHorizontal: 3,
        fontSize: 7.5,
        textAlign: 'center',
    },
    miniCell: {
        flex: 1,
        paddingVertical: 2,
        paddingHorizontal: 3,
        fontSize: 7.5,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
        borderRightWidth: 0.5,
        borderRightColor: GRAY,
    },
    miniCellLast: {
        flex: 1,
        paddingVertical: 2,
        paddingHorizontal: 3,
        fontSize: 7.5,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
    },

    // Bloc droit client — deux tableaux bordés
    rightBlock: {
        width: '52%',
        flexDirection: 'column',
    },
    // Tableau 1 : infos principales
    clientTable: {
        borderWidth: 0.5,
        borderColor: GRAY,
        marginBottom: 3,
    },
    clientRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: GRAY,
    },
    clientRowLast: {
        flexDirection: 'row',
    },
    clientLabel: {
        width: 45,
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 2,
        paddingHorizontal: 4,
        borderRightWidth: 0.5,
        borderRightColor: GRAY,
    },
    clientValue: {
        flex: 1,
        fontSize: 7.5,
        paddingVertical: 2,
        paddingHorizontal: 4,
    },
    clientValueBold: {
        flex: 1,
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 2,
        paddingHorizontal: 4,
    },
    // Tableau 2 : Contact
    contactTable: {
        borderWidth: 0.5,
        borderColor: GRAY,
    },
    contactHeader: {
        borderBottomWidth: 0.5,
        borderBottomColor: GRAY,
        paddingVertical: 1,
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
    },
    contactHeaderText: {
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
    },

    // ── PAX & V/RÉF ──────────────────────────────────────────────────────────
    vrefSection: {
        marginTop: 2,
        marginBottom: 6,
    },
    vrefRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 1.5,
        borderBottomWidth: 0.5,
        borderBottomColor: '#eeeeee',
    },
    vrefLabel: { fontSize: 8, color: BLACK, fontFamily: 'Helvetica-Bold', width: 60 },
    vrefValue: { fontSize: 8, color: BLACK, flex: 1 },

    // ── TABLEAU ARTICLES ─────────────────────────────────────────────────────
    tableWrap: {
        borderWidth: 0.5,
        borderColor: GRAY,
        marginBottom: 8,
    },

    // En-tête tableau
    tHead: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: GRAY,
        backgroundColor: BLACK,
    },
    thCellFirst: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
        paddingVertical: 4,
        paddingHorizontal: 2,
        borderRightWidth: 0.5,
        borderRightColor: 'rgba(255,255,255,0.3)',
        color: '#ffffff',
    },
    thCell: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
        paddingVertical: 4,
        paddingHorizontal: 2,
        borderRightWidth: 0.5,
        borderRightColor: 'rgba(255,255,255,0.3)',
        color: '#ffffff',
    },
    thCellLast: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
        paddingVertical: 4,
        paddingHorizontal: 2,
        color: '#ffffff',
    },

    // Corps table
    tRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e5e7eb',
        minHeight: 14,
    },
    tRowNoBorder: {
        flexDirection: 'row',
        minHeight: 13,
        backgroundColor: '#f8faff',
    },
    tCellBorder: {
        fontSize: 7.5,
        paddingVertical: 2,
        paddingHorizontal: 3,
        borderRightWidth: 0.5,
        borderRightColor: '#999999',
        flexWrap: 'wrap',
        minHeight: 12, // Assurer que le texte s'affiche complètement
    },
    tCellBorderC: {
        fontSize: 7.5,
        paddingVertical: 2,
        paddingHorizontal: 3,
        borderRightWidth: 0.5,
        borderRightColor: '#999999',
        textAlign: 'center',
    },
    tCellBorderR: {
        fontSize: 7.5,
        paddingVertical: 2,
        paddingHorizontal: 3,
        borderRightWidth: 0.5,
        borderRightColor: '#999999',
        textAlign: 'right',
    },
    tCellLast: {
        fontSize: 7.5,
        paddingVertical: 2,
        paddingHorizontal: 3,
        textAlign: 'right',
    },

    // Ligne catégorie (CIRCUIT)
    tCatRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#999999',
        minHeight: 13,
    },
    tCatText: { fontSize: 8, fontFamily: 'Helvetica-Bold', paddingVertical: 2, paddingHorizontal: 3 },

    // Sous-ligne italique
    tSubRow: {
        flexDirection: 'row',
        minHeight: 11,
    },
    tSubText: {
        fontSize: 7.5,
        fontFamily: 'Helvetica-Oblique',
        color: NAVY,
        paddingVertical: 2,
        paddingHorizontal: 3,
    },

    // TOTAL
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        borderTopWidth: 0.5,
        borderTopColor: '#999999',
        paddingTop: 4,
        paddingBottom: 4,
        paddingHorizontal: 6,
    },
    totalLabel: {
        fontSize: 8.5,
        fontFamily: 'Helvetica-Bold',
        marginRight: 20,
        color: NAVY,
    },
    totalValue: {
        fontSize: 8.5,
        fontFamily: 'Helvetica-Bold',
        minWidth: 70,
        textAlign: 'right',
        color: BLUE,
    },

    // ── MONTANT EN LETTRES ───────────────────────────────────────────────────
    lettersRow: {
        marginTop: 6,
        marginBottom: 8,
        backgroundColor: '#ffffff',
        paddingVertical: 4,
        paddingHorizontal: 6,
        borderWidth: 0.5,
        borderColor: '#000000',
    },
    lettersLine: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    lettersPrefix: { fontSize: 8, color: '#000000' },
    lettersValue: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#000000' },

    // ── RÈGLEMENT + SIGNATURE ────────────────────────────────────────────────
    signatureRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 5,
        alignItems: 'flex-start',
    },
    reglLabel: { fontSize: 8, color: NAVY },
    comptoLabel: { fontSize: 8, color: NAVY, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
    signatureImg: { height: 120, width: 240, marginLeft: -30, objectPosition: 'left' },

    // ── COORDONNÉES BANCAIRES ────────────────────────────────────────────────
    bankRow: {
        marginTop: 4,
        paddingTop: 2,
    },
    bankTitle: {
        fontSize: 8.5,
        fontFamily: 'Helvetica-Bold',
        color: NAVY,
        textAlign: 'center',
        textTransform: 'uppercase',
        marginBottom: 3,
    },
    bankTable: {
        borderWidth: 0.5,
        borderColor: '#999999',
    },
    bankRow2: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#999999',
    },
    bankLabel: {
        width: 65,
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 2,
        paddingHorizontal: 4,
        borderRightWidth: 0.5,
        borderRightColor: '#999999',
    },
    bankValue: {
        flex: 1,
        fontSize: 7.5,
        paddingVertical: 2,
        paddingHorizontal: 4,
    },

    // ── FOOTER (absolu en bas de page) ──────────────────────────────────────
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 28,
        right: 28,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 0.5,
        borderTopColor: '#999999',
        paddingTop: 8,
        alignItems: 'flex-start',
    },
    footerLeft: { flexDirection: 'column', flex: 0.6 },
    footerTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: NAVY, marginBottom: 2 },
    footerText: { fontSize: 6.5, color: NAVY, marginTop: 1 },
    footerRight: { flexDirection: 'column', alignItems: 'flex-end', flex: 0.35 },
});

// ─── Composant principal ──────────────────────────────────────────────────────
const FacturePDF = ({ facture, typeDocument = 'FACTURE', bankOption = 'sgbs', deviseOption = 'cfa' }) => {
    const [total, setTotal] = useState(0);
    const [totalPaye, setTotalPaye] = useState(0);

    const CW = deviseOption === 'both' ? CW_BOTH : CW_SINGLE;
    const isEuro = deviseOption === 'euro';
    const exchangeRate = 655.957;

    const displayAmount = (amountCfa) => {
        if (!amountCfa) return '';
        if (isEuro) return toEuro(amountCfa);
        return priceFormatter(amountCfa);
    };

    useEffect(() => {
        if (!facture) return;
        let sum = 0;
        // Somme des billets
        (facture.billets || []).forEach(b => { 
            const pu = Number(b.tarif || 0);
            const qte = Number(b.quantite) || 1;
            const pax = Number(b.nbrPax) || 1;
            sum += (pu * qte * pax); 
        });
        // Somme des frais supplémentaires (uniquement ceux visibles sur le PDF)
        const fraisVisibles = (facture.frais || []).filter(f => 
            !/billets/i.test(f.libelle) && 
            !/interne/i.test(f.nature || f.motif || '') &&
            !/interne/i.test(f.libelle || f.designation || '')
        );
        fraisVisibles.forEach(f => { 
            const pu = Number(f.montant || 0);
            const qte = Number(f.quantite) || 1;
            const pax = Number(f.nbrPax || f.pax || facture?.dossier?.pax || 1);
            sum += (pu * qte * pax); 
        });
        setTotal(sum);

        let paye = 0;
        (facture.paiements || []).forEach(p => { paye += Number(p.montant || 0); });
        setTotalPaye(paye);
    }, [facture]);

    if (!facture) return null;

    const client = facture.client || {};
    let clientName;
    let clientAdresse = client.adresse || '';
    let clientVille = client.ville || '';
    let clientEmail = client.email || '';
    let clientPays = client.pays || '';
    let clientTelephone = client.telephone || '';

    const isComptant = !facture.client || client.code === 'COMPTANT';
    
    if (isComptant) {
        clientName = facture.nomClientComptant || 'CLIENT COMPTANT';
        clientAdresse = facture.adresseClientComptant || '';
        clientVille = facture.villeClientComptant || '';
        clientEmail = facture.emailClientComptant || '';
        clientPays = facture.paysClientComptant || '';
        clientTelephone = facture.telephoneClientComptant || '';
    } else if (client.structure) {
        clientName = client.nom || '';
    } else {
        clientName = `${client.prenom || ''} ${client.nom || ''}`.trim();
    }

    // --- Noms des paxs (Rooming) ---
    const getPaxNames = () => {
        const dossier = facture.dossier;
        if (!dossier) return clientName;
        
        const rooming = dossier.rooming || [];
        if (rooming.length === 0) return clientName;

        const firstPax = rooming[0];
        let baseName = '';

        if (dossier.isGroup) {
            // Pour un groupe, 'nom' contient souvent le nom du groupe
            baseName = firstPax.nom || firstPax.prenom || 'Groupe';
        } else {
            // Pour des individus
            baseName = `${firstPax.prenom || ''} ${firstPax.nom || ''}`.trim();
        }

        const totalPax = Number(dossier.pax) || rooming.length;
        if (totalPax > 1) {
            return `${baseName} X${totalPax}`;
        }
        return baseName || clientName;
    };
    const nomDesPaxs = getPaxNames();

    const commentaireAffiche = facture.commentaire || '';


    const billets = (facture.billets || []);
    // Filtrage des frais internes (ne doivent pas apparaître sur le PDF client)
    const fraisHors = (facture.frais || []).filter(f => 
        !/billets/i.test(f.libelle) && 
        !/interne/i.test(f.nature || f.motif || '') &&
        !/interne/i.test(f.libelle || f.designation || '')
    );

    // Dérive le label de référencement depuis le préfixe du numéro de facture
    // FV = BILLETTERIE, FC = CIRCUIT, FE = EXCURSION, FS = SÉJOUR, FT = TRANSFERT
    const getCategorieLabel = () => {
        // 1. Si la facture est liée à un Dossier (même si ce n'est pas un objet complet)
        if (facture.dossier) {
            // Si l'objet dossier contient le type explicite
            if (typeof facture.dossier === 'object' && facture.dossier.type) {
                return facture.dossier.type.toUpperCase();
            }
            // Sinon, on se fie au préfixe car on SAIT que c'est une facture de production
            const num = (facture.numeroFacture || '').toUpperCase();
            if (num.startsWith('FE')) return 'EXCURSION';
            if (num.startsWith('FS')) return 'SÉJOUR';
            if (num.startsWith('FT')) return 'TRANSFERT';
            return 'CIRCUIT'; // Par défaut pour FC ou autre
        }

        // 2. Si le backend fournit explicitement typeActivite
        if (facture.typeActivite) {
            return facture.typeActivite.toUpperCase();
        }

        // 3. Si la facture n'a PAS de dossier rattaché, c'est de la Billetterie (ou assimilé)
        if (!facture.dossier) {
            if (billets.length > 0 || window.location.href.includes('billeterie')) {
                return 'BILLETTERIE';
            }
        }

        const num = (facture.numeroFacture || '').toUpperCase();
        if (num.startsWith('FV')) return 'BILLETTERIE';
        
        return 'CIRCUIT';
    };
    const categorieLabel = getCategorieLabel();

    // Utilitaire : cellule avec bordure droite
    const BC = (w, txt, align = 'left', bold = false) => (
        <Text style={{
            width: w,
            fontSize: 7.5,
            paddingVertical: 2,
            paddingHorizontal: 3,
            borderRightWidth: 0.5,
            borderRightColor: '#999999',
            textAlign: align,
            fontFamily: bold ? 'Helvetica-Bold' : 'Helvetica',
        }}>{txt || ''}</Text>
    );
    const BL = (w, txt, align = 'right', bold = false) => (
        <Text style={{
            width: w,
            fontSize: 7.5,
            paddingVertical: 2,
            paddingHorizontal: 3,
            textAlign: align,
            fontFamily: bold ? 'Helvetica-Bold' : 'Helvetica',
        }}>{txt || ''}</Text>
    );

    // Cellule en-tête
    const TH = (w, txt, last = false) => (
        <Text style={{
            width: w,
            fontSize: 7,
            fontFamily: 'Helvetica-Bold',
            textAlign: 'center',
            paddingVertical: 4,
            paddingHorizontal: 2,
            color: '#ffffff',
            ...(last ? {} : { borderRightWidth: 0.5, borderRightColor: 'rgba(255,255,255,0.3)' }),
        }}>{txt}</Text>
    );

    return (
        <Document>
            <Page size="A4" style={s.page}>

                {/* ══ LOGO ══════════════════════════════════════════════ */}
                <View style={s.logoRow}>
                    <Image style={s.logo} source={logo} />
                </View>

                {/* ══ BLOC EN-TÊTE : DEUX COLONNES (Facture + Client) ══ */}
                <View style={s.headerRow}>
                    
                    {/* Bloc gauche : Info Facture */}
                    <View style={s.leftBlock}>
                        <View style={s.factureBadge}>
                            <Text style={s.factureBadgeText}>{typeDocument.toUpperCase()}</Text>
                        </View>

                        <View style={[s.miniTable, { marginTop: 2 }]}>
                            <View style={s.miniHead}>
                                <Text style={s.miniCellHead}>Numéro</Text>
                                <Text style={s.miniCellHead}>Date</Text>
                                <Text style={s.miniCellHeadLast}>Dossier</Text>
                            </View>
                            <View style={s.miniRow}>
                                <Text style={s.miniCell}>{facture.numeroFacture || '-'}</Text>
                                <Text style={s.miniCell}>{dateFormatter(facture.createdAt) || '-'}</Text>
                                <Text style={s.miniCellLast}>{facture.numeroDossier || '-'}</Text>
                            </View>
                        </View>

                        {!!commentaireAffiche && (
                            <View style={{ marginTop: 4, padding: 3, border: '0.5px solid #999999', borderRadius: 2 }}>
                                <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold' }}>Motif :</Text>
                                <Text style={{ fontSize: 7, marginTop: 1 }}>{commentaireAffiche}</Text>
                            </View>
                        )}
                    </View>

                    {/* Bloc droit : Info Client & Compte Auxiliaire SAGE */}
                    <View style={s.rightBlock}>
                        <View style={s.clientTable}>
                            <View style={s.clientRow}>
                                <Text style={s.clientLabel}>Code</Text>
                                <Text style={s.clientValueBold}>{getClientSageAuxCode(client, clientName)}</Text>
                            </View>
                            <View style={s.clientRow}>
                                <Text style={s.clientLabel}>Nom :</Text>
                                <Text style={s.clientValueBold}>{clientName}</Text>
                            </View>
                            <View style={s.clientRow}>
                                <Text style={s.clientLabel}>Adresse :</Text>
                                <Text style={s.clientValue}>{clientAdresse || '-'}</Text>
                            </View>
                            <View style={s.clientRow}>
                                <Text style={s.clientLabel}>Ville :</Text>
                                <Text style={s.clientValue}>{clientVille || '-'}</Text>
                            </View>
                            <View style={s.clientRow}>
                                <Text style={s.clientLabel}>Pays</Text>
                                <Text style={s.clientValue}>{clientPays || '-'}</Text>
                            </View>
                            <View style={s.clientRow}>
                                <Text style={s.clientLabel}>Tél :</Text>
                                <Text style={s.clientValue}>{clientTelephone || '-'}</Text>
                            </View>
                            <View style={s.clientRowLast}>
                                <Text style={s.clientLabel}>Mail :</Text>
                                <Text style={[s.clientValue, { color: BLACK }]}>{clientEmail || '-'}</Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ══ PAX & V/RÉF ══════════════════════════════════════ */}
                <View style={s.vrefSection}>
                    <View style={s.vrefRow}>
                        <Text style={s.vrefLabel}>Nom des pax : </Text>
                        <Text style={s.vrefValue}>{nomDesPaxs || ''}</Text>
                    </View>
                    <View style={s.vrefRow}>
                        <Text style={s.vrefLabel}>V/réf :</Text>
                        <Text style={s.vrefValue}>{facture.numeroDossier || ''}</Text>
                    </View>
                </View>

                {/* ══ TABLEAU ARTICLES ══════════════════════════════════ */}
                <View style={s.tableWrap}>

                    {/* En-tête */}
                    <View style={s.tHead}>
                        {TH(CW.ref, 'Référence')}
                        {TH(CW.design, 'Désignation')}
                        {TH(CW.nature, 'Nature . Motif')}
                        {TH(CW.nbrPax, 'Nbre\nPax')}
                        {TH(CW.qte, 'Qté')}
                        {deviseOption === 'both' ? (
                            <>
                                {TH(CW.prixCFA, 'Prix\nUnit.')}
                                {TH(CW.prixEUR, 'Prix\nUnit.EURO')}
                            </>
                        ) : (
                            TH(CW.prix, `Prix\nUnit.${isEuro ? 'EURO' : ''}`)
                        )}
                        {TH(CW.montant, 'Montant', true)}
                    </View>

                    {/* ── Groupe catégorie pour les billets ──────────────── */}
                    {billets.length > 0 && (
                        <>
                            {/* Ligne catégorie */}
                            <View style={s.tCatRow}>
                                {BC(CW.ref, categorieLabel, 'left', true)}
                                {BC(CW.design, '')}
                                {BC(CW.nature, '')}
                                {BC(CW.nbrPax, '')}
                                {BC(CW.qte, '')}
                                {deviseOption === 'both' ? (
                                    <>
                                        {BC(CW.prixCFA, '')}
                                        {BC(CW.prixEUR, '')}
                                    </>
                                ) : (
                                    BC(CW.prix, '')
                                )}
                                {BL(CW.montant, '')}
                            </View>

                            {billets.map((b, idx) => {
                                const prixCFA = Number(b.tarif) || 0;
                                const prixEUR = toEuro(prixCFA);
                                const qte = Number(b.quantite) || 1;
                                const nbrPax = Number(b.nbrPax) || 1;

                                return (
                                    <React.Fragment key={idx}>

                                        {/* Nom du billet / Bénéficiaire */}
                                        <View style={s.tRowNoBorder}>
                                            {BC(CW.ref, b.numero || b.numeroVol || '')}
                                            <Text style={{
                                                width: CW.design,
                                                fontSize: 7.5,
                                                fontFamily: 'Helvetica-Bold',
                                                paddingVertical: 2,
                                                paddingLeft: 5,
                                                borderRightWidth: 0.5,
                                                borderRightColor: '#999999',
                                            }}>{b.nom || ''}{b.trajet ? ` — ${b.trajet}` : ''}</Text>
                                            {BC(CW.nature, b.commentaire || facture.commentaire || '')}
                                            {BC(CW.nbrPax, '', 'center')}
                                            {BC(CW.qte, '', 'center')}
                                            {deviseOption === 'both' ? (
                                                <>
                                                    {BC(CW.prixCFA, '', 'right')}
                                                    {BC(CW.prixEUR, '', 'right')}
                                                </>
                                            ) : (
                                                BC(CW.prix, '', 'right')
                                            )}
                                            {BL(CW.montant, '', 'right')}
                                        </View>

                                        {/* Dates */}
                                        {b.dateVol && (
                                            <View style={s.tSubRow}>
                                                {BC(CW.ref, '')}
                                                <Text style={{
                                                    width: CW.design,
                                                    fontSize: 7.5,
                                                    fontFamily: 'Helvetica-Oblique',
                                                    paddingVertical: 1,
                                                    paddingLeft: 14,
                                                    borderRightWidth: 0.5,
                                                    borderRightColor: '#999999',
                                                }}>{`From ${b.dateVol}${b.dateVolRetour ? ` to ${b.dateVolRetour}` : ''}`}</Text>
                                                {BC(CW.nature, '')}
                                                {BC(CW.nbrPax, '', 'center')}
                                                {BC(CW.qte, '', 'center')}
                                                {deviseOption === 'both' ? (
                                                    <>
                                                        {BC(CW.prixCFA, '', 'right')}
                                                        {BC(CW.prixEUR, '', 'right')}
                                                    </>
                                                ) : (
                                                    BC(CW.prix, '', 'right')
                                                )}
                                                {BL(CW.montant, '', 'right')}
                                            </View>
                                        )}

                                        {/* Ligne chambre/type hébergement avec prix */}
                                        {b.classe ? (
                                            <View style={s.tRow}>
                                                {BC(CW.ref, '')}
                                                {BC(CW.design, b.classe || '', 'left')}
                                                {BC(CW.nature, b.nature || '')}
                                                {BC(CW.nbrPax, String(nbrPax), 'center')}
                                                {BC(CW.qte, String(qte), 'center')}
                                                {deviseOption === 'both' ? (
                                                    <>
                                                        {BC(CW.prixCFA, priceFormatter(prixCFA), 'right')}
                                                        {BC(CW.prixEUR, prixEUR, 'right')}
                                                    </>
                                                ) : (
                                                    BC(CW.prix, displayAmount(prixCFA), 'right')
                                                )}
                                                {BL(CW.montant, displayAmount(prixCFA * qte * nbrPax), 'right')}
                                            </View>
                                        ) : (
                                            <View style={s.tRow}>
                                                {BC(CW.ref, '')}
                                                {BC(CW.design, '', 'left')}
                                                {BC(CW.nature, '')}
                                                {BC(CW.nbrPax, String(nbrPax), 'center')}
                                                {BC(CW.qte, String(qte), 'center')}
                                                {deviseOption === 'both' ? (
                                                    <>
                                                        {BC(CW.prixCFA, priceFormatter(prixCFA), 'right')}
                                                        {BC(CW.prixEUR, prixEUR, 'right')}
                                                    </>
                                                ) : (
                                                    BC(CW.prix, displayAmount(prixCFA), 'right')
                                                )}
                                                {BL(CW.montant, displayAmount(prixCFA * qte * nbrPax), 'right')}
                                            </View>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            {/* Lignes frais hors-billets */}
                            {fraisHors.map((f, idx) => {
                                const fpax = Number(f.nbrPax || f.pax || facture?.dossier?.pax || 1);
                                const fqte = Number(f.quantite || 1);
                                const fpu = Number(f.montant || f.prixUnitaire || 0);
                                const fMontant = fpu * fqte * fpax;

                                return (
                                <View key={`f-${idx}`} style={s.tRow}>
                                    {BC(CW.ref, f.reference || facture.numeroDossier || '')}
                                    {BC(CW.design, f.libelle || f.designation || '', 'left')}
                                    {BC(CW.nature, f.motif || f.nature || '')}
                                    {BC(CW.nbrPax, String(fpax), 'center')}
                                    {BC(CW.qte, String(fqte), 'center')}
                                    {deviseOption === 'both' ? (
                                        <>
                                            {BC(CW.prixCFA, fpu ? priceFormatter(fpu) : '', 'right')}
                                            {BC(CW.prixEUR, fpu ? toEuro(fpu) : '', 'right')}
                                        </>
                                    ) : (
                                        BC(CW.prix, fpu ? displayAmount(fpu) : '', 'right')
                                    )}
                                    {BL(CW.montant, fMontant ? displayAmount(fMontant) : '', 'right')}
                                </View>
                                );
                            })}
                        </>
                    )}

                    {/* ── Si aucun billet : frais seuls ────────────────── */}
                    {billets.length === 0 && fraisHors.map((f, idx) => {
                        const fpax = Number(f.nbrPax || f.pax || facture?.dossier?.pax || 1);
                        const fqte = Number(f.quantite || 1);
                        const fpu = Number(f.montant || f.prixUnitaire || 0);
                        const fMontant = fpu * fqte * fpax;
                        
                        return (
                        <View key={idx} style={s.tRow}>
                            {BC(CW.ref, f.reference || f.categorie || facture.numeroDossier || '')}
                            {BC(CW.design, f.libelle || f.designation || '', 'left')}
                            {BC(CW.nature, f.nature || '')}
                            {BC(CW.nbrPax, String(fpax), 'center')}
                            {BC(CW.qte, String(fqte), 'center')}
                            {deviseOption === 'both' ? (
                                <>
                                    {BC(CW.prixCFA, priceFormatter(fpu), 'right')}
                                    {BC(CW.prixEUR, toEuro(fpu), 'right')}
                                </>
                            ) : (
                                BC(CW.prix, displayAmount(fpu), 'right')
                            )}
                            {BL(CW.montant, displayAmount(fMontant), 'right')}
                        </View>
                        );
                    })}

                    {/* ── Total ────────────────────────────────────────── */}
                    <View style={s.totalRow}>
                        <Text style={s.totalLabel}>Total Facture</Text>
                        <Text style={s.totalValue}>{displayAmount(total)}</Text>
                    </View>
                    <View style={s.totalRow}>
                        <Text style={s.totalLabel}>Total Payé</Text>
                        <Text style={s.totalValue}>{displayAmount(totalPaye)}</Text>
                    </View>
                    <View style={{...s.totalRow, backgroundColor: '#f5f5f5', paddingTop: 5, paddingBottom: 5, borderBottomWidth: 0.5, borderBottomColor: '#000000'}}>
                        <Text style={{...s.totalLabel, color: '#000000'}}>Reste à payer</Text>
                        <Text style={{...s.totalValue, color: '#000000'}}>{displayAmount(total - totalPaye)}</Text>
                    </View>
                </View>

                {/* ── Spacer supprimé — plus de page vierge ────────── */}

                {/* ══ MONTANT EN LETTRES ════════════════════════════════ */}
                <View style={s.lettersRow}>
                    <View style={s.lettersLine}>
                        <Text style={s.lettersPrefix}>
                            {'Arrêtée la présente facture à la somme de : '}
                        </Text>
                        <Text style={s.lettersValue}>
                            {((toFrench(Math.round(isEuro ? total / exchangeRate : total)) || '').replace(/FRANCS CFA/gi, '').trim() + (isEuro ? ' EUROS' : '')).toUpperCase()}
                        </Text>
                    </View>
                </View>

                {/* ══ EN VOTRE RÈGLEMENT | SIGNATURE + COORDONNÉES BANCAIRES ══ */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8, gap: 12 }}>

                    {/* Gauche : règlement + signature */}
                    <View style={{ flex: 0.48 }}>
                        <Text style={s.reglLabel}>En votre aimable règlement</Text>
                        <View style={{ alignItems: 'flex-start', marginTop: 4 }}>
                            <Text style={s.comptoLabel}>Comptabilité clients</Text>
                            <Image style={s.signatureImg} source={signature} />
                        </View>
                    </View>

                    {/* Droite : coordonnées bancaires */}
                    <View style={{ flex: 0.48 }}>
                        {(() => {
                            const showSgbs = bankOption === 'sgbs';
                            const showOrabank = bankOption === 'orabank';
                            const showBankInfo = showSgbs || showOrabank;

                            if (!showBankInfo) return null;

                            return (
                                <View>
                                    <Text style={[s.bankTitle, { textAlign: 'left', marginBottom: 4 }]}>Coordonnées Bancaires</Text>
                                    
                                    {showSgbs && (
                                        <View style={s.bankTable}>
                                            <View style={s.bankRow2}>
                                                <Text style={s.bankLabel}>Banque</Text>
                                                <Text style={s.bankValue}>SGBS SENEGAL</Text>
                                            </View>
                                            <View style={s.bankRow2}>
                                                <Text style={s.bankLabel}>IBAN</Text>
                                                <Text style={s.bankValue}>SN08 SN011 09032 006109453209 50</Text>
                                            </View>
                                            <View style={s.bankRow2}>
                                                <Text style={s.bankLabel}>BIC/SWIFT</Text>
                                                <Text style={s.bankValue}>SGSNSNDAXXX</Text>
                                            </View>
                                            <View style={{ ...s.bankRow2, borderBottomWidth: 0 }}>
                                                <Text style={{ ...s.bankLabel, borderRightWidth: 0.5 }}>Compte</Text>
                                                <Text style={s.bankValue}>006109453209 50</Text>
                                            </View>
                                        </View>
                                    )}

                                    {showSgbs && showOrabank && <View style={{ height: 6 }} />}

                                    {showOrabank && (
                                        <View style={s.bankTable}>
                                            <View style={s.bankRow2}>
                                                <Text style={s.bankLabel}>Banque</Text>
                                                <Text style={s.bankValue}>ORABANK SENEGAL</Text>
                                            </View>
                                            <View style={s.bankRow2}>
                                                <Text style={s.bankLabel}>IBAN</Text>
                                                <Text style={s.bankValue}>SN08 SN175 09415 045618100201 22</Text>
                                            </View>
                                            <View style={s.bankRow2}>
                                                <Text style={s.bankLabel}>BIC/SWIFT</Text>
                                                <Text style={s.bankValue}>ORBKSNDA</Text>
                                            </View>
                                            <View style={{ ...s.bankRow2, borderBottomWidth: 0 }}>
                                                <Text style={{ ...s.bankLabel, borderRightWidth: 0.5 }}>Compte</Text>
                                                <Text style={s.bankValue}>045618100201</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            );
                        })()}
                    </View>

                </View>

                {/* ══ FOOTER ═══════════════════════════════════════════ */}
                <View style={s.footer} fixed>
                    <View style={s.footerLeft}>
                        <Text style={s.footerTitle}>AFRICA FOR TOURISM</Text>
                        <Text style={s.footerText}>
                            SARL au capital de 5.000.000 FCFA. Licence 019258 délivrée par le MTTA
                        </Text>
                        <Text style={s.footerText}>
                            NINEA : 002442424 - RC : SN THS 2014 M 2461
                        </Text>
                        <Text style={s.footerText}>
                            Saly Tapées, route de Ngaparou, face pharmacie Salve Regina
                        </Text>
                    </View>

                    <View style={s.footerRight}>
                        <Text style={s.footerText}>BP 624 SALY</Text>
                        <Text style={s.footerText}>+221 77 649 93 22</Text>
                        <Text style={s.footerText}>+221 33 957 81 81</Text>
                        <Text style={s.footerText}>contact@africa4tourism.com</Text>
                        <Text style={s.footerText}>www.africa4tourism.com</Text>
                    </View>
                </View>

            </Page>
        </Document>
    );
};

export default FacturePDF;
